import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  Menu,
  X,
  Dumbbell,
  Users,
  Target,
  Heart,
  Clock,
  MapPin,
  Phone,
  Instagram,
  Facebook,
  ChevronRight,
  Star,
  Zap,
  Award,
  Check,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";

// Assets
const ASSETS = {
  hero: "https://customer-assets.emergentagent.com/job_gym-portal-33/artifacts/cje6curw_image.png",
  exterior:
    "https://customer-assets.emergentagent.com/job_gym-portal-33/artifacts/ahf7rlvj_image.png",
  interior:
    "https://customer-assets.emergentagent.com/job_gym-portal-33/artifacts/p1zaxbqy_image.png",
  texture:
    "https://images.unsplash.com/photo-1633319987325-aae32f3d8dd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwY29uY3JldGUlMjB0ZXh0dXJlJTIwd2FsbCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzczMTc2MDg3fDA&ixlib=rb-4.1.0&q=85",
  athlete:
    "https://images.unsplash.com/photo-1709315957145-a4bad1feef28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxneW0lMjBhdGhsZXRlJTIwbGlmdGluZyUyMGhlYXZ5JTIwd2VpZ2h0cyUyMGRyYW1hdGljJTIwbGlnaHRpbmclMjBibGFjayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzczMTc2MDg5fDA&ixlib=rb-4.1.0&q=85",
};

// Contact Info
const CONTACT = {
  whatsapp: "50688999997",
  phone: "8899 9997",
  location: "200 mts este del centro comercial Guadalupe, Costa Rica",
  instagram: "https://www.instagram.com/nx_xtremezone/",
  facebook: "https://www.facebook.com/profile.php?id=61577019272836",
  googleMaps: "https://maps.app.goo.gl/h3ekoqnoDPVJ4gSdA",
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Inicio", href: "#inicio" },
    { name: "Servicios", href: "#servicios" },
    { name: "Beneficios", href: "#beneficios" },
    { name: "Instalaciones", href: "#instalaciones" },
    { name: "Planes", href: "#planes" },
    { name: "Horarios", href: "#horarios" },
    { name: "Galería", href: "#galeria" },
    { name: "Contacto", href: "#contacto" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-black/90 nav-blur py-3" : "bg-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a
            href="#inicio"
            className="flex items-center gap-2"
            data-testid="logo"
          >
            <div className="relative">
              <span className="font-heading font-black text-3xl tracking-tighter">
                <span className="text-white">NX</span>
                <span className="text-[#FFD700]">XTREME</span>
              </span>
              <span className="absolute -bottom-1 left-0 text-[10px] text-zinc-400 tracking-widest font-medium">
                ZONE
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm font-medium text-zinc-300 hover:text-[#FFD700] transition-colors uppercase tracking-wide"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                className="bg-[#FFD700] text-black font-bold hover:bg-[#E6C200] btn-shine rounded-none"
                data-testid="nav-cta-button"
              >
                ÚNETE AHORA
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden mt-4 pb-4 mobile-menu-enter"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="text-lg font-medium text-zinc-300 hover:text-[#FFD700] transition-colors uppercase tracking-wide py-2 border-b border-zinc-800"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <a
                href={`https://wa.me/${CONTACT.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2"
              >
                <Button className="w-full bg-[#FFD700] text-black font-bold hover:bg-[#E6C200] rounded-none py-6">
                  ÚNETE AHORA
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="inicio"
      className="hero-height relative flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${ASSETS.hero})` }}
      />

      {/* Overlay */}
      <div className="absolute inset-0 hero-gradient" />

      {/* Noise texture */}
      <div className="absolute inset-0 noise-overlay" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-3xl">
          <div className="animate-fade-in-up">
            <span className="inline-block bg-[#FFD700] text-black font-bold text-sm px-4 py-2 mb-6 uppercase tracking-widest">
              Guadalupe, Costa Rica
            </span>
          </div>

          <h1 className="font-heading font-black text-5xl sm:text-6xl md:text-7xl lg:text-8xl uppercase tracking-tighter text-white leading-[0.9] animate-fade-in-up delay-100">
            ENTRENA
            <br />
            <span className="text-[#FFD700]">SIN LÍMITES</span>
          </h1>

          <p className="mt-6 text-lg sm:text-xl text-zinc-300 max-w-xl animate-fade-in-up delay-200">
            Equipo moderno, entrenadores profesionales y el ambiente perfecto
            para superar tus límites.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
            <a
              href={`https://wa.me/${CONTACT.whatsapp}?text=Hola,%20quiero%20información%20sobre%20las%20membresías`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                className="bg-[#FFD700] text-black font-bold text-lg hover:bg-[#E6C200] btn-shine rounded-none px-8 py-6 w-full sm:w-auto"
                data-testid="hero-cta-button"
              >
                EMPIEZA HOY
                <ChevronRight className="ml-2" size={20} />
              </Button>
            </a>
            <a href="#planes">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white font-bold text-lg hover:bg-white hover:text-black rounded-none px-8 py-6 w-full sm:w-auto bg-transparent"
                data-testid="hero-plans-button"
              >
                VER PLANES
              </Button>
            </a>
          </div>

          {/* Stats */}
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-md animate-fade-in-up delay-400">
            <div>
              <span className="font-accent text-4xl sm:text-5xl text-[#FFD700]">
                +70
              </span>
              <p className="text-sm text-zinc-400 mt-1">Máquinas</p>
            </div>
            <div>
              <span className="font-accent text-4xl sm:text-5xl text-[#FFD700]">
                17H
              </span>
              <p className="text-sm text-zinc-400 mt-1">Horario L-V</p>
            </div>
            <div>
              <span className="font-accent text-4xl sm:text-5xl text-[#FFD700]">
                7
              </span>
              <p className="text-sm text-zinc-400 mt-1">Días/Semana</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-[#FFD700] rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Dumbbell,
      title: "Musculación",
      description:
        "Área completa de pesas con equipo de última generación para tu entrenamiento de fuerza.",
      image: "/images/galeria11.jpg",
      image2: "/images/musculacion.jpeg",
      size: "col-span-1 row-span-2",
    },
    {
      icon: Users,
      title: "Clases Grupales",
      description:
        "Clases de baile, funcionales y entrenamientos grupales guiados por instructores certificados.",
      image: "/images/clasegrup.jpg",
      size: "col-span-1 row-span-1",
    },
    {
      icon: Target,
      title: "Boxeo",
      description:
        "Saco de boxeo y área funcional para tu entrenamiento de combate.",
      image: "/images/boxeo.jpeg",
      size: "col-span-1 row-span-1",
    },
    {
      icon: Heart,
      title: "Zona de Cardio",
      description:
        "Área equipada con escaladoras, caminadoras, bicicletas, elípticas y arc trainer para mejorar tu resistencia cardiovascular.",
      image: "/images/cardio.jpeg",
      size: "col-span-1 row-span-1",
    },
    {
      icon: Award,
      title: "Entrenadores Pro",
      description: "Asesoría personalizada con profesionales certificados.",
      image: "/images/entrenadores.jpg",
      size: "col-span-1 row-span-1",
    },
  ];

  return (
    <section
      id="servicios"
      className="section-padding relative overflow-hidden"
      style={{
        backgroundImage: `url(${ASSETS.texture})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="services-section"
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#09090b]/95" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-left mb-16">
          <span className="text-[#FFD700] font-bold text-sm uppercase tracking-widest">
            Nuestros Servicios
          </span>
          <h2 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter mt-4 text-white yellow-underline">
            TODO LO QUE
            <br />
            <span className="text-[#FFD700]">NECESITAS</span>
          </h2>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((service, index) => (
            <Card
              key={index}
              className={`bg-zinc-900/80 border-industrial p-6 sm:p-8 card-hover bento-item ${
                index === 0 ? "md:row-span-2" : ""
              }`}
            >
              <div className="h-full flex flex-col">
                <div className="w-14 h-14 bg-[#FFD700]/10 rounded-md flex items-center justify-center mb-6">
                  <service.icon className="text-[#FFD700]" size={28} />
                </div>

                <h3 className="font-heading font-bold text-2xl uppercase tracking-tight text-white mb-3">
                  {service.title}
                </h3>

                <p className="text-zinc-400">{service.description}</p>

                <div className="mt-6 flex flex-col gap-6">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-48 object-cover rounded-md"
                  />

                  {service.image2 && (
                    <img
                      src={service.image2}
                      alt={service.title}
                      className="w-full h-48 object-cover rounded-md"
                    />
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
const FacilitiesSection = () => {
  const facilities = [
    {
      title: "Zona de refrigerio",
      image: "/images/comedor.jpeg",
    },
    {
      title: "Lockers para clientes",
      image: "/images/lockers.jpeg",
    },
    {
      title: "Área de niños (servicio exclusivo)",
      image: "/images/ninos.jpeg",
    },
    {
      title: "Vestidores y duchas (agua caliente)",
      image: "/images/camerinos.jpeg",
    },
    {
      title: "Parqueo para clientes",
      image: "/images/parqueo1.jpeg",
    },
    {
      title: "Zona de Medición",
      image: "/images/medicion2.jpeg",
    },
    {
      title: "Sillón masajeador",
      image: "/images/sillon.jpeg",
    },
  ];

  return (
    <section id="instalaciones" className="section-padding bg-zinc-900/40">
      <div className="max-w-7xl mx-auto">
        <div className="text-left mb-16">
          <span className="text-[#FFD700] font-bold text-sm uppercase tracking-widest">
            Instalaciones
          </span>

          <h2 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter mt-4 text-white yellow-underline">
            MÁS
            <br />
            <span className="text-[#FFD700]">COMODIDAD</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {facilities.map((item, index) => (
            <div
              key={index}
              className="rounded-md overflow-hidden border border-zinc-800 bg-zinc-900 hover:scale-[1.02] transition flex flex-col"
            >
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-80 object-cover"
              />

              <div className="p-4 mt-auto">
                <h3 className="text-white font-semibold text-lg">
                  {item.title}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
// Plans Section
const PlansSection = () => {
  const featuredPlans = [
    {
      name: "Mes",
      subtitle: "Plan más popular",
      price: "20,000",
      features: [
        "Acceso completo al gimnasio",
        "Rutina personalizada",
        "Clases grupales incluidas",
        "Acompañamiento de instructores",
        "Medición corporal sin costo",
        "Clases de baile",
      ],
      highlight: true,
    },
    {
      name: "Trimestre",
      subtitle: "Ahorra con 3 meses",
      price: "54,000",
      features: [
        "Acceso completo al gimnasio",
        "Rutina personalizada",
        "Clases grupales incluidas",
        "Clases de baile",
        "Medición corporal sin costo",
      ],
      highlight: false,
    },
    {
      name: "Semestre",
      subtitle: "6 meses de entrenamiento",
      price: "102,000",
      features: [
        "Acceso completo al gimnasio",
        "Rutina personalizada",
        "Clases grupales incluidas",
        "Clases de baile",
        "Medición corporal sin costo",
      ],
      highlight: false,
    },
    {
      name: "Anualidad",
      subtitle: "Mejor precio del año",
      price: "192,000",
      features: [
        "Acceso completo al gimnasio",
        "Rutina personalizada",
        "Clases grupales incluidas",
        "Clases de baile",
        "Medición corporal sin costo",
      ],
      highlight: false,
    },
  ];

  const extras = [{ name: "Nutricionista" }, { name: "Sillón masajeador" }];

  return (
    <section
      id="planes"
      className="section-padding bg-[#09090b]"
      data-testid="plans-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-left mb-16">
          <span className="text-[#FFD700] font-bold text-sm uppercase tracking-widest">
            Tarifas 2026
          </span>

          <h2 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter mt-4 text-white yellow-underline">
            ELIGE TU
            <br />
            <span className="text-[#FFD700]">PLAN</span>
          </h2>
        </div>

        {/* PLANES DESTACADOS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {featuredPlans.map((plan, index) => (
            <Card
              key={index}
              className={`relative overflow-hidden card-hover ${
                plan.highlight
                  ? "plan-featured"
                  : "bg-zinc-900/50 border-industrial"
              }`}
            >
              {plan.highlight && (
                <div className="absolute top-0 right-0 bg-[#FFD700] text-black font-bold text-xs px-4 py-2 uppercase tracking-wider">
                  Popular
                </div>
              )}

              <div className="p-6 sm:p-8">
                <div className="mb-6">
                  <h3 className="font-heading font-bold text-2xl uppercase tracking-tight text-white">
                    {plan.name}
                  </h3>
                  <p className="text-zinc-500 text-sm">{plan.subtitle}</p>
                </div>

                <div className="mb-8">
                  <div className="flex items-baseline gap-1">
                    <span className="text-zinc-500 text-lg">₡</span>
                    <span
                      className={`font-accent text-5xl ${
                        plan.highlight ? "text-[#FFD700]" : "text-white"
                      }`}
                    >
                      {plan.price}
                    </span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check
                        className={`flex-shrink-0 mt-0.5 ${
                          plan.highlight ? "text-[#FFD700]" : "text-zinc-500"
                        }`}
                        size={18}
                      />
                      <span className="text-zinc-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={`https://wa.me/${CONTACT.whatsapp}?text=Hola,%20quiero%20información%20sobre%20el%20plan%20${plan.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    className={`w-full font-bold rounded-none py-6 ${
                      plan.highlight
                        ? "bg-[#FFD700] text-black hover:bg-[#E6C200]"
                        : "bg-zinc-800 text-white hover:bg-zinc-700"
                    }`}
                  >
                    ELEGIR PLAN
                  </Button>
                </a>
              </div>
            </Card>
          ))}
        </div>

        {/* SERVICIOS ADICIONALES */}
        <div>
          <h3 className="text-2xl font-bold text-white mb-6">
            Servicios adicionales
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            {extras.map((extra, index) => (
              <div
                key={index}
                className="flex justify-between bg-zinc-900 border border-zinc-800 p-4 rounded-md"
              >
                <span className="text-zinc-300">{extra.name}</span>
                <span className="text-[#FFD700] font-bold">Disponible</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Schedule Section
const ScheduleSection = () => {
  return (
    <section
      id="horarios"
      className="section-padding bg-zinc-900/50"
      data-testid="schedule-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-left mb-16">
          <span className="text-[#FFD700] font-bold text-sm uppercase tracking-widest">
            Abierto 7 días
          </span>
          <h2 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter mt-4 text-white yellow-underline">
            NUESTROS
            <br />
            <span className="text-[#FFD700]">HORARIOS</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Schedule Tabs */}
          <div>
            <Tabs defaultValue="weekdays" className="w-full">
              <TabsList className="bg-zinc-800 rounded-none p-1 mb-8 w-full grid grid-cols-2">
                <TabsTrigger
                  value="weekdays"
                  className="rounded-none data-[state=active]:bg-[#FFD700] data-[state=active]:text-black font-bold uppercase tracking-wider"
                  data-testid="schedule-tab-weekdays"
                >
                  Entre Semana
                </TabsTrigger>
                <TabsTrigger
                  value="weekend"
                  className="rounded-none data-[state=active]:bg-[#FFD700] data-[state=active]:text-black font-bold uppercase tracking-wider"
                  data-testid="schedule-tab-weekend"
                >
                  Fin de Semana
                </TabsTrigger>
              </TabsList>

              <TabsContent value="weekdays" className="mt-0">
                <Card className="bg-zinc-900/80 border-industrial p-8">
                  <div className="flex items-center justify-between mb-6 pb-6 border-b border-zinc-800">
                    <div className="flex items-center gap-4">
                      <Clock className="text-[#FFD700]" size={24} />
                      <span className="font-heading font-bold text-xl uppercase text-white">
                        Lunes a Viernes
                      </span>
                    </div>
                  </div>
                  <div className="text-center py-8">
                    <span className="font-accent text-6xl sm:text-7xl text-white">
                      5:00 AM
                    </span>
                    <span className="font-accent text-4xl text-zinc-500 mx-4">
                      —
                    </span>
                    <span className="font-accent text-6xl sm:text-7xl text-[#FFD700]">
                      10:00 PM
                    </span>
                  </div>
                  <p className="text-center text-zinc-400 mt-4">
                    17 horas de servicio continuo
                  </p>
                </Card>
              </TabsContent>

              <TabsContent value="weekend" className="mt-0">
                <div className="space-y-4">
                  <Card className="bg-zinc-900/80 border-industrial p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Clock className="text-[#FFD700]" size={24} />
                        <span className="font-heading font-bold text-xl uppercase text-white">
                          Sábado
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="font-accent text-3xl sm:text-4xl text-white">
                          8:00 AM
                        </span>
                        <span className="font-accent text-xl text-zinc-500 mx-2">
                          —
                        </span>
                        <span className="font-accent text-3xl sm:text-4xl text-[#FFD700]">
                          4:00 PM
                        </span>
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-zinc-900/80 border-industrial p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Clock className="text-[#FFD700]" size={24} />
                        <span className="font-heading font-bold text-xl uppercase text-white">
                          Domingo
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="font-accent text-3xl sm:text-4xl text-white">
                          8:00 AM
                        </span>
                        <span className="font-accent text-xl text-zinc-500 mx-2">
                          —
                        </span>
                        <span className="font-accent text-3xl sm:text-4xl text-[#FFD700]">
                          2:00 PM
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Image */}
          <div className="hidden lg:block">
            <div className="relative img-zoom rounded-md overflow-hidden">
              <img
                src={ASSETS.exterior}
                alt="NX Xtreme Zone exterior"
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <p className="text-white font-heading font-bold text-2xl uppercase">
                  NX XTREME ZONE
                </p>
                <p className="text-zinc-300 text-sm flex items-center gap-2 mt-2">
                  <MapPin size={14} className="text-[#FFD700]" />
                  Guadalupe, Costa Rica
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
const BenefitsSection = () => {
  const benefits = [
    {
      text: "Rutinas personalizadas",
      image: "/images/rutina.jpeg",
    },
    {
      text: "Acompañamiento de instructores",
      image: "/images/instructores.jpeg",
    },
    {
      text: "Medición corporal sin costo",
      image: "/images/medicion1.jpeg",
    },
    {
      text: "Clases grupales incluidas",
      image: "/images/clasesfuncionales.jpeg",
    },
    {
      text: "Sin limites",
      image: "/images/limite.jpeg",
    },
    {
      text: "Domigos de entrenamiento con Amigos",
      image: "/images/amigo.jpeg",
    },
    {
      text: "Acceso seguro con reconocimiento facial",
      image: "/images/facial.png",
    },
  ];
  return (
    <section id="beneficios" className="section-padding bg-[#09090b]">
      <div className="max-w-7xl mx-auto">
        <div className="text-left mb-16">
          <span className="text-[#FFD700] font-bold text-sm uppercase tracking-widest">
            ¿Por qué elegirnos?
          </span>

          <h2 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter mt-4 text-white">
            BENEFICIOS
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <div
              key={index}
             className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden hover:scale-[1.02] transition flex flex-col"
    >
              <img
                src={benefit.image}
                alt={benefit.text}
                className="w-full full-h object-cover"
              />

              <div className="p-5 flex-1 flex items-end">
        <p className="text-white font-semibold flex items-center gap-2">
          <span className="text-[#FFD700]">✓</span>
          {benefit.text}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
// Gallery Section
const GallerySection = () => {
  const images = [
    { src: "/images/box.jpeg", alt: "Boxeo", tall: true },
    {
      src: "/images/galeria11.jpg",
      alt: "Máquinas de entrenamiento",
      tall: false,
    },
    { src: "/images/galeria3.jpeg", alt: "Área funcional", tall: false },
    { src: "/images/parqueo.jpeg", alt: "Clientes entrenando", tall: true },
    { src: "/images/galeria4.jpeg", alt: "Clientes entrenando", tall: true },
    { src: "/images/galeria5.jpeg", alt: "Máquinas modernas", tall: false },
    { src: "/images/patrocinio.jpg", alt: "Patrocinio", tall: false },
    { src: "/images/zumba.jpg", alt: "Zumba", tall: false },
    { src: "/images/cardio2.jpg", alt: "Zona de cardio", tall: false },
    { src: "/images/parqueo2.jpeg", alt: "Parqueo", tall: false },
    { src: "/images/parqueo3.jpeg", alt: "Parqueo", tall: false },
    { src: "/images/galeria9.jpeg", alt: "Medición", tall: false },
  ];
  const droneVideos = [
    {
      src: "/videos/dron1.mov",
      title: "Toma aérea NX Xtreme Zone",
    },
    {
      src: "/videos/dron2.mov",
      title: "Vista aérea del gimnasio",
    },
    {
      src: "/videos/gymdron.mp4",
      title: "Vista aérea del gimnasio",
    },
  ];

  return (
    <section
      id="galeria"
      className="section-padding bg-[#09090b]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-left mb-16">
          <span className="text-[#FFD700] font-bold text-sm uppercase tracking-widest">
            Conoce nuestras instalaciones
          </span>

          <h2 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter mt-4 text-white yellow-underline">
            GALERÍA
          </h2>
        </div>

        {/* Masonry Grid */}
        <div className="gallery-masonry">
          {images.map((image, index) => (
            <div
              key={index}
              className={`img-zoom rounded-md overflow-hidden ${
                image.tall ? "aspect-[3/4]" : "aspect-video"
              }`}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>

        {/* Drone Videos */}
        <div className="mt-20">
          <h3 className="font-heading font-bold text-3xl uppercase text-white mb-8">
            TOMAS AÉREAS
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {droneVideos.map((video, index) => (
              <div
                key={index}
                className="rounded-md overflow-hidden border border-zinc-800"
              >
                <video
                  src={video.src}
                  controls
                  muted
                  loop
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-zinc-400 mb-6">
            Síguenos en redes para más contenido
          </p>

          <div className="flex justify-center gap-4">
            <a
              href={CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-zinc-800 hover:bg-[#FFD700] hover:text-black rounded-md flex items-center justify-center transition-all"
            >
              <Instagram size={24} />
            </a>

            <a
              href={CONTACT.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-zinc-800 hover:bg-[#FFD700] hover:text-black rounded-md flex items-center justify-center transition-all"
            >
              <Facebook size={24} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section
      id="contacto"
      className="section-padding bg-zinc-900/50"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Info */}
          <div>
            <span className="text-[#FFD700] font-bold text-sm uppercase tracking-widest">
              Contáctanos
            </span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl md:text-6xl uppercase tracking-tighter mt-4 text-white yellow-underline">
              ÚNETE A LA
              <br />
              <span className="text-[#FFD700]">ZONA EXTREMA</span>
            </h2>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFD700]/10 rounded-md flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[#FFD700]" size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">Ubicación</h4>
                  <p className="text-zinc-400">{CONTACT.location}</p>
                  <a
                    href={CONTACT.googleMaps}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FFD700] text-sm font-medium hover:underline inline-flex items-center gap-1 mt-2"
                    data-testid="contact-map-link"
                  >
                    Ver en Google Maps <ChevronRight size={14} />
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFD700]/10 rounded-md flex items-center justify-center flex-shrink-0">
                  <Phone className="text-[#FFD700]" size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">WhatsApp</h4>
                  <p className="text-zinc-400">{CONTACT.phone}</p>
                  <a
                    href={`https://wa.me/${CONTACT.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FFD700] text-sm font-medium hover:underline inline-flex items-center gap-1 mt-2"
                    data-testid="contact-whatsapp-link"
                  >
                    Escríbenos ahora <ChevronRight size={14} />
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FFD700]/10 rounded-md flex items-center justify-center flex-shrink-0">
                  <Zap className="text-[#FFD700]" size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-white mb-1">Redes Sociales</h4>
                  <div className="flex gap-3 mt-2">
                    <a
                      href={CONTACT.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-400 hover:text-[#FFD700] transition-colors"
                      data-testid="contact-instagram"
                    >
                      <Instagram size={24} />
                    </a>
                    <a
                      href={CONTACT.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-zinc-400 hover:text-[#FFD700] transition-colors"
                      data-testid="contact-facebook"
                    >
                      <Facebook size={24} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="w-full mt-10">
            <div className="rounded-md overflow-hidden border border-zinc-800 w-full min-h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d245.6187089757628!2d-84.06265890250553!3d9.942332552341439!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8fa0e5001b4cba5d%3A0x6be8b86f6794babc!2sNX%20Xtreme%20Zone!5e0!3m2!1ses!2scr!4v1773433018772!5m2!1ses!2scr"
                className="w-full h-[400px] border-0"
                loading="lazy"
                title="Ubicación NX Xtreme Zone"
              />
            </div>

            <a
              href="https://maps.google.com/?q=NX+Xtreme+Zone"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center mt-4 bg-[#FFD700] text-black font-bold py-3 rounded-md"
            >
              Abrir en Google Maps
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-black py-12 px-4" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="text-center md:text-left">
            <span className="font-heading font-black text-2xl tracking-tighter">
              <span className="text-white">NX</span>
              <span className="text-[#FFD700]">XTREME</span>
              <span className="text-zinc-500 text-sm ml-1">ZONE</span>
            </span>
            <p className="text-zinc-500 text-sm mt-2">Entrena sin límites</p>
          </div>

          {/* Links */}
          <div className="flex items-center gap-6">
            <a
              href={CONTACT.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-[#FFD700] transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram size={20} />
            </a>
            <a
              href={CONTACT.facebook}
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 hover:text-[#FFD700] transition-colors"
              data-testid="footer-facebook"
            >
              <Facebook size={20} />
            </a>
          </div>

          {/* Copyright */}
          <p className="text-zinc-600 text-sm">
            © 2026 NX Xtreme Zone. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  return (
    <a
      href={`https://wa.me/${CONTACT.whatsapp}?text=Hola,%20quiero%20información%20sobre%20el%20gimnasio`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] hover:bg-[#20BA5C] rounded-full flex items-center justify-center shadow-lg whatsapp-pulse transition-transform hover:scale-110"
      data-testid="whatsapp-button"
    >
      <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>
  );
};

// Home Page
const Home = () => {
  return (
    <div className="min-h-screen bg-[#09090b]">
      <Navbar />
      <HeroSection />
      <ServicesSection />
      <BenefitsSection />
      <FacilitiesSection />
      <PlansSection />
      <ScheduleSection />
      <GallerySection />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
